import { AuthorizationStatus, CheckState, Liveness } from './enums';

/**
 * The questions asked of a finding's check state, in one place. Each was
 * written out at every screen and service that asks it, and the two columns
 * were read together in four of them.
 */
export interface CheckedState {
  readonly liveness: Liveness;
  readonly checkState?: CheckState | null;
}

/** Off the host entirely — the only outcome we may call a removal. */
export function isGone(liveness: Liveness): boolean {
  return liveness === Liveness.GONE;
}

/** Nobody has looked yet, so there is nothing to report either way. */
export function notCheckedYet(finding: CheckedState): boolean {
  return finding.checkState === CheckState.NOT_CHECKED || finding.liveness === Liveness.UNKNOWN;
}

/** We looked and were turned away, which is not the same as finding nothing. */
export function checkWasBlocked(finding: CheckedState): boolean {
  return finding.checkState === CheckState.FETCH_BLOCKED || finding.liveness === Liveness.BLOCKED;
}

/** The four states a screen shows. A page that came back reads as live. */
export type ShownLiveness =
  | typeof Liveness.UNKNOWN
  | typeof Liveness.BLOCKED
  | typeof Liveness.GONE
  | typeof Liveness.LIVE;

/**
 * The state to show a finding in, with `checkState` folded into `liveness`.
 * Three screens and a shared component each had this four-branch ladder,
 * differing only in the words at the end. The words are per-screen.
 */
export function shownLiveness(finding: CheckedState): ShownLiveness {
  if (notCheckedYet(finding)) return Liveness.UNKNOWN;
  if (checkWasBlocked(finding)) return Liveness.BLOCKED;
  if (isGone(finding.liveness)) return Liveness.GONE;
  return Liveness.LIVE;
}

/**
 * Whether we may actually file for this profile. Asked at nine call sites in
 * the API and both frontends, every one of them as `=== 'verified'` against a
 * column whose vocabulary lived in a service file the browser cannot import.
 */
export function ownershipProven(status: AuthorizationStatus): boolean {
  return status === AuthorizationStatus.VERIFIED;
}
